import { Form, Input, Button, Modal, Grid, Select } from "antd";
import { useEffect, useMemo } from "react";
import type { FormInstance } from "antd";

import { normalizeApiError } from "../../../shared/errors/normalizeApiError";
import { toFormFieldErrors } from "../../../shared/errors/fieldErrors";
import { appNotification } from "../../../shared/notifications/appNotification";

import type { CustomerLineOption, GroupOrderOption } from "../model/types";

interface LineFormValues {
  customer_key?: string;
  line_code: string;
  line_name?: string;
  staff_name?: string;
  company_out_name?: string;
  group_id?: string;
  phone_number?: string;
}

interface Props {
  open: boolean;
  loading?: boolean;
  form?: FormInstance<LineFormValues>;
  initialValues?: Partial<LineFormValues>;
  customerOptions: { label: string; value: string }[];
  companyNameOptions: { label: string; value: string | number }[];
  groupOrderOptions: GroupOrderOption[];
  customerLineOptions: CustomerLineOption[];
  onSearchCustomer: (value: string) => void;
  onSearchCompanyName?: (value: string) => void;
  onCustomerChange?: (customerKey?: string) => void;
  onCompanyNameChange?: (companyName?: string | number) => void;
  onGroupChange?: (groupId: string) => void;
  onOpenCustomerOptions?: () => void;
  onClose: () => void;
  onSubmit: (values: LineFormValues) => Promise<void>;
}

export const LineForm = ({
  open,
  loading,
  form,
  initialValues,
  customerOptions,
  companyNameOptions,
  groupOrderOptions,
  customerLineOptions,
  onSearchCustomer,
  onSearchCompanyName,
  onCustomerChange,
  onCompanyNameChange,
  onGroupChange,
  onOpenCustomerOptions,
  onClose,
  onSubmit,
}: Props) => {
  const [internalForm] = Form.useForm();
  const activeForm = form || internalForm;
  const screens = Grid.useBreakpoint();

  const isMobile = !screens.md;
  const isEditMode = !!initialValues;
  const selectedLineName = Form.useWatch("line_name", activeForm);

  const lineNameOptions = useMemo(() => {
    const names = new Set(
      customerLineOptions
        .map((option) => option.line_name?.trim())
        .filter((name): name is string => Boolean(name)),
    );
    return Array.from(names).map((name) => ({ label: name, value: name }));
  }, [customerLineOptions]);

  const lineCodeOptions = useMemo(() => {
    const options = selectedLineName
      ? customerLineOptions.filter(
          (option) => option.line_name?.trim() === selectedLineName,
        )
      : customerLineOptions;
    return Array.from(new Set(options.map((option) => option.line_id))).map(
      (lineId) => ({ label: lineId, value: lineId }),
    );
  }, [customerLineOptions, selectedLineName]);

  useEffect(() => {
    if (open) {
      activeForm.resetFields();
      if (initialValues) activeForm.setFieldsValue(initialValues);
    }
  }, [open, initialValues, activeForm]);

  const handleCustomerChange = (value?: string) => {
    onCustomerChange?.(value);
  };

  const handleCompanyNameChange = (value?: string | number) => {
    onCompanyNameChange?.(value);
  };

  const handleGroupChange = (groupId: string) => {
    activeForm.setFieldValue("group_id", groupId);
    onGroupChange?.(groupId);
  };

  const handleLineCodeChange = (lineId: string) => {
    const matches = customerLineOptions.filter(
      (option) => option.line_id === lineId,
    );
    const lineNames = Array.from(
      new Set(
        matches
          .map((option) => option.line_name?.trim())
          .filter((name): name is string => Boolean(name)),
      ),
    );
    const phoneNumbers = Array.from(
      new Set(
        matches
          .map((option) => option.phone_number?.trim())
          .filter((phone): phone is string => Boolean(phone)),
      ),
    );
    const lineName = lineNames.length === 1 ? lineNames[0] : undefined;

    activeForm.setFieldsValue({
      line_name: lineName,
      staff_name: lineName,
      phone_number: phoneNumbers.length === 1 ? phoneNumbers[0] : undefined,
    });
  };

  const handleLineNameChange = (lineName: string) => {
    const lineIds = Array.from(
      new Set(
        customerLineOptions
          .filter((option) => option.line_name?.trim() === lineName)
          .map((option) => option.line_id),
      ),
    );
    if (lineIds.length === 1) {
      activeForm.setFieldValue("line_code", lineIds[0]);
      handleLineCodeChange(lineIds[0]);
      return;
    }
    activeForm.setFieldsValue({
      line_code: undefined,
      staff_name: lineName,
      phone_number: undefined,
    });
  };

  const handleFinish = async (values: LineFormValues) => {
    try {
      await onSubmit(values);
      activeForm.resetFields();
    } catch (error: unknown) {
      const apiError = normalizeApiError(error);
      console.error("Error creating line:", apiError);
      const aliases: Record<string, string> = {
        line_id: "line_code",
        representative_name: "staff_name",
      };
      const fieldErrors = toFormFieldErrors(apiError).map((field) => ({
        ...field,
        name: field.name.map((part) =>
          typeof part === "string" ? aliases[part] || part : part,
        ),
      }));
      if (fieldErrors.length) activeForm.setFields(fieldErrors as never);

      appNotification.error({
        message: "エラー",
        description:
          apiError.message || "保存に失敗しました。もう一度お試しください。",
      });
    }
  };

  return (
    <Modal
      title={initialValues ? "お客様情報編集" : "お客様情報追加"}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      centered
      width={isMobile ? "100%" : 700}
      styles={{
        body: {
          paddingBottom: 12,
          overflow: "auto",
          maxHeight: "calc(100vh - 200px)",
        },
      }}
    >
      <Form form={activeForm} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          label="MTコード・枝番"
          name="customer_key"
          rules={[
            {
              required: !initialValues?.company_out_name,
              message: "MTコード・枝番を選択してください",
            },
          ]}
        >
          <Select
            allowClear
            showSearch
            placeholder="選択してください"
            onSearch={onSearchCustomer}
            filterOption={false}
            options={customerOptions}
            onChange={handleCustomerChange}
            onOpenChange={(isOpen) => {
              if (
                isOpen &&
                !activeForm.getFieldValue("customer_key") &&
                !activeForm.getFieldValue("company_out_name")
              )
                onOpenCustomerOptions?.();
            }}
          />
        </Form.Item>

        <Form.Item
          label="会社名"
          name="company_out_name"
          rules={[
            {
              required: true,
              message: "会社名を選択してください",
            },
          ]}
        >
          <Select
            allowClear
            showSearch
            placeholder="選択してください"
            onSearch={onSearchCompanyName || onSearchCustomer}
            filterOption={false}
            options={companyNameOptions}
            onChange={handleCompanyNameChange}
            onOpenChange={(isOpen) => {
              if (isOpen && !activeForm.getFieldValue("company_out_name"))
                onOpenCustomerOptions?.();
            }}
          />
        </Form.Item>

        <Form.Item
          label="LINEユーザー名"
          name="line_name"
          rules={[
            {
              required: true,
              message: "LINEユーザー名を入力してください",
            },
          ]}
        >
          {isEditMode ? (
            <Input allowClear />
          ) : (
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="選択してください"
              options={lineNameOptions}
              onChange={handleLineNameChange}
            />
          )}
        </Form.Item>

        <Form.Item
          label="LINEユーザーID"
          name="line_code"
          rules={[
            {
              required: true,
              message: "LINEユーザーIDを入力してください",
            },
          ]}
        >
          {isEditMode ? (
            <Input allowClear readOnly disabled />
          ) : (
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="選択してください"
              options={lineCodeOptions}
              onChange={handleLineCodeChange}
            />
          )}
        </Form.Item>

        {isEditMode ? (
          <Form.Item
            label="LINEグループ名"
            name="group_name"
            rules={[
              {
                required: true,
                message: "LINEグループ名を入力してください",
              },
            ]}
          >
            <Input allowClear readOnly disabled />
          </Form.Item>
        ) : (
          <>
            <Form.Item
              label="LINEグループ名"
              name="group_id"
              rules={[
                {
                  required: true,
                  message: "LINEグループ名を選択してください",
                },
              ]}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="選択してください"
                options={groupOrderOptions.map((option) => ({
                  label: option.group_name,
                  value: option.group_id,
                }))}
                onChange={handleGroupChange}
              />
            </Form.Item>
          </>
        )}

        <Form.Item
          label="ご担当者名"
          name="staff_name"
          rules={[
            {
              required: true,
              message: "ご担当者名を入力してください",
            },
          ]}
        >
          <Input allowClear />
        </Form.Item>

        <Form.Item
          label="電話番号"
          name="phone_number"
          rules={[
            // {
            //   required: true,
            //   message: "電話番号を入力してください",
            // },
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }

                const phone = value.replace(/[\s-]/g, "");

                const vnRegex = /^(?:\+84|84|0)(3|5|7|8|9)\d{8}$/;

                const jpRegex = /^(?:\+81|81|0)(70|80|90|3|6)\d{8,9}$/;

                if (vnRegex.test(phone) || jpRegex.test(phone)) {
                  return Promise.resolve();
                }

                return Promise.reject(
                  new Error("電話番号の形式が正しくありません"),
                );
              },
            },
          ]}
        >
          <Input allowClear inputMode="tel" />
        </Form.Item>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <Button onClick={onClose}>キャンセル</Button>

          <Button type="primary" htmlType="submit" loading={loading}>
            保存
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
